import type { Component } from "vue";
import {
  Compass,
  Film,
  Flame,
  Gift,
  GraduationCap,
  LibraryBig,
  Lightbulb, 
  Percent,
  Radio,
  ReceiptText,
  Sparkles,
  UserRound,
  Users,
} from "@lucide/vue";
import type { MsgKey } from "@/i18n/strings";

/** 原型顶栏/侧栏主色 */
export const APP_BRAND_PINK = "#FF007F";

/** 侧栏品牌名，对齐原型 */
export const APP_BRAND_NAME = "TidakTidur";

/** 精选页路由，对应 views/featured */
export const FEATURED_PATH = "/featured";

/** 推荐页路由，对应 views/recommend */
export const RECOMMEND_PATH = "/recommend";

/**
 * 未登录（游客）在精选里最多能看到的作品条数。
 *
 * <p>需求：游客「只能看，不能点赞/评论/收藏/私信」，想看更多要登录。
 * 因此游客侧请求的 `pageSize` 被夹到该值，且不展示「加载更多」，
 * 改为在列表底部给一个「登录查看更多」引导。</p>
 *
 * <p>⚠️ 这是**纯前端限制**（用户 2026-09-23 拍板）：后端 `/ai-video/featured/list`
 * 仍是免签免登白名单，直接用地址栏或抓包调接口、把 `pageSize` 传大，仍能拿到全量。
 * 若将来要真正防绕过，需要后端在接口内自行解析 token（无有效 token 时把 pageSize 夹到 10），
 * 或把该接口从白名单挪出、另开一个只返回前 10 条的公开接口。</p>
 */
export const FEATURED_GUEST_LIMIT = 10;

/** 创作菜单入口：/create 入口页（自主创作 + 元素工坊 磁贴） */
export const CREATE_ENTRY_PATH = "/create";

/** 自主创作子页：原 /ai-creation，2026-11-03 迁到 /create 下 */
export const CREATE_AI_CREATION_PATH = "/create/ai-creation";

/** AI 直播。功能页和维护页都挂在这个路径下面 */
export const LIVE_PATH = "/ai-live";

/** 当前路径是不是 AI 直播模块，含子页面 */
export function isAiLivePath(path: string): boolean {
  return path === LIVE_PATH || path.startsWith(`${LIVE_PATH}/`);
}

/**
 * 未登录可直接访问的前台路径（不含落地页/登录页）。
 *
 * <p>`/me`、`/following`、`/orders`、`/resource`、`/inspiration`、`CREATE_ENTRY_PATH` 与
 * `CREATE_AI_CREATION_PATH` 仍在列：未登录可以进入访客页。
 * 进入这些页时由路由守卫自动弹出登录框（用户 2026-10-01；`/orders` 为 2026-10-20 追加；
 * `/resource` 资产库为 2026-10-27 追加；`/inspiration` 灵感中心为 2026-11-03 追加）。</p>
 */
export const PUBLIC_ROUTE_PATHS = [
  "/",
  FEATURED_PATH,
  RECOMMEND_PATH,
  "/drama",
  "/resource",
  "/inspiration",
  "/following",
  "/me",
  "/orders",
  CREATE_ENTRY_PATH,
  CREATE_AI_CREATION_PATH,
] as const;

/**
 * 视频详情页路由前缀，对应原型 `/app/watch/:id`。
 *
 * <p>精选页 / 关注页点卡片都会进入该页。原型 `routes.tsx` 里 `watch/:id` 直接挂在
 * `Layout` 下且没有任何鉴权 loader，游客也能观看（只是互动操作不可用），
 * 这里保持同样口径：不进 `PUBLIC_ROUTE_PATHS`（那是精确匹配的静态路径表），
 * 而是由 {@link isPublicRoutePath} 做前缀放行。</p>
 */
export const WATCH_PATH_PREFIX = "/watch/";

/** 短剧播放页 `/drama/:id`，游客可进，付费集在页内解锁 */
export const DRAMA_PLAY_PATH_PREFIX = "/drama/";

/**
 * 判断路径是否为「游客可直接访问」的前台路由。
 *
 * <p>静态路径走精确匹配；带参动态路径（`/watch/:id`）走前缀匹配——
 * 路由守卫拿到的是解析后的真实路径（如 `/watch/72`），不可能等于路由模板。</p>
 */
export function isPublicRoutePath(path: string): boolean {
  // 报名链接给未登录的人打开，精确匹配，不弹登录框
  if (path === "/training/enroll") return true;
  if ((PUBLIC_ROUTE_PATHS as readonly string[]).includes(path)) return true;
  return path.startsWith(WATCH_PATH_PREFIX) || path.startsWith(DRAMA_PLAY_PATH_PREFIX) || isAiLivePath(path);
}

/**
 * 未登录进入时停留在访客页，并自动弹出登录框的一级入口。
 *
 * <p>创作入口页 / 自主创作子页、关注、我的、资产库、灵感中心只匹配入口本身。
 * AI 直播包含其下的功能页，访客停在模块里并弹出登录框。</p>
 */
export function isGuestAuthPromptPath(path: string): boolean {
  return (
    path === CREATE_ENTRY_PATH ||
    path === CREATE_AI_CREATION_PATH ||
    path === "/following" ||
    path === "/me" ||
    path === "/orders" ||
    path === "/resource" ||
    path === "/inspiration" ||
    path === RECOMMEND_PATH ||
    isAiLivePath(path)
  );
}

/** 一级导航项 */
export interface AppPrimaryNavItem {
  /** 路由地址 */
  path: string;
  /** 菜单文案 i18n key，展示时按当前语言翻译 */
  labelKey: MsgKey;
  /** Lucide 图标 */
  icon: Component;
  /** 是否精确匹配（精选页 /featured） */
  end?: boolean;
  /**
   * 未登录点击时进入该页访客态，并由路由守卫弹出登录框。
   *
   * <p>创作、关注、我的使用。</p>
   */
  requiresAuth?: boolean;
  /**
   * 仅平台管理员可见（{@code globalStore.userInfo?.role === "admin"}）。
   *
   * <p>用于「订单」这类运营入口：普通用户侧边栏**根本不渲染**该项。
   * 与路由 {@code meta.requiresAdmin} 形成双重把关——前者防误入，
   * 后者防手敲 URL 直闯（守卫会把非管理员弹回 `/home`）。</p>
   */
  adminOnly?: boolean;
  /**
   * 企业子账户（role=2）不可见：豆池与订阅由主账户管理，子账户无独立订单。
   *
   * <p>仅隐藏菜单项，路由与页面保留——子账户手敲 URL 仍可进，后端接口按本人权限兜底。</p>
   */
  hideForSubAccount?: boolean;
  /**
   * 菜单项不展示，但路由与页面代码保留。
   *
   * <p>2026-10-19：分佣、权益先从侧栏收起来，代码不删，后续可能重新开放。
   * `/admin/commission`、`/admin/benefits` 与 `meta.requiresAdmin` 守卫全部保留，
   * 管理员手敲 URL 仍可进入。</p>
   */
  hidden?: boolean;
}

/**
 * 一级菜单：精选 / 推荐 / 短剧 / 创作 / 资产库 / AI 直播 / 关注 / 我的 / 订单。
 *
 * <p>灵感中心菜单入口于 2026-11-05 以注释方式隐藏，路由与页面保留，手敲 /inspiration 仍可进入。</p>
 *
 * <p>「订单」对所有人可见（{@code requiresAuth}）：游客点击弹出登录框；
 * 登录后按角色分流——普通用户看自己的订单，管理员进同一页由页面内部分流
 * 渲染财务统计视图（也是线下充值「确认到账」的操作台）。</p>
 *
 * <p>「资产库」复用既有 `/resource` 页面（素材库 / 商品库 / 数字人），
 * 未登录展示访客态并弹出登录框，与创作口径一致。</p>
 *
 * <p>「分佣」「权益」标了 {@code hidden}：侧栏与移动底栏都不渲染，路由和页面保留。</p>
 */
export const PRIMARY_NAV_ITEMS: AppPrimaryNavItem[] = [
  // 2026-11-05：灵感中心菜单入口暂时隐藏，路由与页面保留
  // { path: "/inspiration", labelKey: "navInspiration", icon: Lightbulb, requiresAuth: true },
  { path: FEATURED_PATH, labelKey: "navFeatured", icon: Compass, end: true },
  { path: RECOMMEND_PATH, labelKey: "navForYou", icon: Flame, end: true, requiresAuth: true },
  { path: "/drama", labelKey: "navDrama", icon: Film },
  { path: CREATE_ENTRY_PATH, labelKey: "navCreate", icon: Sparkles, requiresAuth: true },
  { path: "/resource", labelKey: "navAssetLibrary", icon: LibraryBig, requiresAuth: true },
  { path: LIVE_PATH, labelKey: "navLive", icon: Radio, requiresAuth: true },
  { path: "/following", labelKey: "navFollowing", icon: Users, requiresAuth: true },
  { path: "/me", labelKey: "navMe", icon: UserRound, requiresAuth: true },
  { path: "/orders", labelKey: "navOrders", icon: ReceiptText, requiresAuth: true, hideForSubAccount: true },
  { path: "/admin/training", labelKey: "navTraining", icon: GraduationCap, adminOnly: true },
  { path: "/admin/commission", labelKey: "navCommission", icon: Percent, adminOnly: true, hidden: true },
  { path: "/admin/benefits", labelKey: "navBenefits", icon: Gift, adminOnly: true, hidden: true },
];

/**
 * 按登录态过滤一级菜单：先去掉 {@link AppPrimaryNavItem.hidden}，
 * 再按 {@link AppPrimaryNavItem.adminOnly} 去掉非管理员不可见的项，
 * 最后按 {@link AppPrimaryNavItem.hideForSubAccount} 去掉企业子账户不可见的项。
 *
 * <p>登录/登出会即时反映（依赖 {@code globalStore.userInfo} 响应式），
 * 无需刷新页面。抽成函数便于侧栏与移动底栏共用同一口径。</p>
 *
 * @param isAdmin 当前登录用户是否平台管理员（role=admin）
 * @param isSubAccount 当前登录用户是否企业子账户（role=2，与 tiers/profile 页口径一致）
 */
export function getVisiblePrimaryNavItems(isAdmin: boolean, isSubAccount = false): AppPrimaryNavItem[] {
  return PRIMARY_NAV_ITEMS.filter((item) => !item.hidden && (!item.adminOnly || isAdmin) && (!item.hideForSubAccount || !isSubAccount));
}

/**
 * 当前是否在自主创作页（创作菜单只对应该页，不含工作台/资产库等）。
 */
export function isCreateSectionPath(path: string): boolean {
  return path === CREATE_ENTRY_PATH || path.startsWith(`${CREATE_ENTRY_PATH}/`);
}
